import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Users, CheckCircle, XCircle, Clock, LogOut, Eye, Search, Filter,
  Calendar, Upload, FileText, BookOpen, ChevronDown, X, Mic2, Plus, Trash2, Edit, Lock, Unlock,
  Shield, CreditCard, Image, Mail, Send, RotateCcw, UserX, Award, Edit3, BellRing, LogIn, Sparkles, DollarSign
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import AdminEditUserModal from '../components/AdminEditUserModal';
import AdminEditRegistrationModal from '../components/AdminEditRegistrationModal';
import CreatePartnerModal from '../components/CreatePartnerModal';
import PostponementCampaignModal from '../components/PostponementCampaignModal';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminTopBar from '../components/admin/AdminTopBar';
import AdminOverview from '../components/admin/AdminOverview';
import AdminCheckin from '../components/admin/AdminCheckin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const scheduleSessionNames = {
  s1: 'Registration & Welcome Coffee', s2: 'Opening Ceremony & Keynote',
  s3: 'Panel: Breaking the Silence', s4: 'Networking Lunch',
  s5: 'Workshop: Healing Through Art', s6: 'Legal Rights & Protection',
  s7: 'Welcome Dinner', s8: 'Morning Meditation', s9: 'Keynote: Rebuilding Self-Worth',
  s10: 'Workshop: CBT Tools', s11: 'Lunch & Peer Support', s12: 'Panel: Workplace Support',
  s13: 'Workshop: Financial Independence', s14: 'Cultural Evening',
  s15: 'Morning Yoga', s16: 'Keynote: From Victim to Victor',
  s17: 'Workshop: Support Network', s18: 'Farewell Lunch',
  s19: 'Action Planning', s20: 'Closing Ceremony',
};

const AdminDashboard = () => {
  const { language, toggleLanguage } = useLanguage();
  const { user, token, logout, startImpersonation, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [partnerPayouts, setPartnerPayouts] = useState([]);
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [showPostponement, setShowPostponement] = useState(false);
  const [serverStats, setServerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Deep-link: if URL has ?tab=invitations&reg=<id>, auto-switch tab and open registration
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const regId = params.get('reg');
    if (tab) setActiveTab(tab);
    if (regId) {
      // fetch full registration details when ready
      const intv = setInterval(async () => {
        if (registrations.length > 0) {
          const r = registrations.find(x => x.id === regId);
          if (r) setSelectedRegistration(r);
          clearInterval(intv);
        }
      }, 400);
      setTimeout(() => clearInterval(intv), 8000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations]);
  const [schedules, setSchedules] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadForm, setUploadForm] = useState({ targetUserId: '', title: '', description: '', file: null });
  const [resourceForm, setResourceForm] = useState({ title: '', description: '', resourceType: 'general', file: null });
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [settings, setSettings] = useState({ speakers_locked: true, program_locked: true });
  const [adminSpeakers, setAdminSpeakers] = useState([]);
  const [adminSessions, setAdminSessions] = useState([]);
  const [speakerForm, setSpeakerForm] = useState({ name: '', role_en: '', role_fr: '', bio_en: '', bio_fr: '', topic_en: '', topic_fr: '', image: '' });
  const [sessionForm, setSessionForm] = useState({ time: '', title_en: '', title_fr: '', session_type: 'session', speaker: '', day: 1 });
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [incompleteNote, setIncompleteNote] = useState('');
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewingUserHistory, setViewingUserHistory] = useState(null); // {user, history}
  const [editingUser, setEditingUser] = useState(null);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [sendingReminderId, setSendingReminderId] = useState(null);

  const content = {
    en: {
      title: "Admin Dashboard",
      subtitle: "Manage conference registrations",
      tabs: { registrations: "Registrations", schedules: "Attendee Schedules", documents: "Documents", resources: "Upload Resources", manageSpeakers: "Speakers", manageProgram: "Program" },
      all: "All", pending: "Pending", approved: "Approved", rejected: "Rejected",
      search: "Search by name or email...",
      name: "Name", email: "Email", status: "Status", date: "Date", actions: "Actions",
      view: "View", approve: "Approve", reject: "Reject", logout: "Sign Out",
      editAction: "Edit",
      sendReminderAction: "Payment Reminder",
      reminderSent: "Payment reminder sent to",
      noRegistrations: "No registrations found",
      registrationDetails: "Registration Details", close: "Close",
      age: "Age", phone: "Phone", city: "City", country: "Country",
      occupation: "Occupation", reason: "Reason for attending",
      heardAbout: "Heard about us", specialNeeds: "Special needs",
      emergencyContact: "Emergency contact", emergencyPhone: "Emergency phone",
      schedulesTitle: "Attendee Schedule Selections",
      schedulesSubtitle: "See which sessions each attendee has added to their personal schedule",
      noSchedules: "No attendees have saved their schedule yet.",
      sessions: "sessions selected",
      docTitle: "Send Document to Attendee",
      docSubtitle: "Upload visa invitation letters, communications, or any personal document for a specific attendee",
      selectUser: "Select Attendee",
      docTitleLabel: "Document Title",
      docDesc: "Description / Message",
      uploadFile: "Upload File",
      send: "Send Document",
      allDocuments: "All Sent Documents",
      noDocuments: "No documents sent yet.",
      resTitle: "Upload Conference Resource",
      resSubtitle: "Upload resources (program guide, prep kit, etc.) for all attendees to download",
      resTitleLabel: "Resource Title",
      resDesc: "Description",
      resType: "Resource Type",
      uploadResource: "Upload Resource",
      sentTo: "Sent to",
      type: "Type",
      speakersTitle: "Manage Speakers",
      speakersLocked: "Speakers section is LOCKED - users see 'locked' message",
      speakersUnlocked: "Speakers section is UNLOCKED - users can see all speakers",
      lockSpeakers: "Lock Speakers",
      unlockSpeakers: "Unlock Speakers",
      addSpeaker: "Add Speaker",
      editSpeaker: "Edit Speaker",
      saveSpeaker: "Save Speaker",
      deleteSpeaker: "Delete",
      nameLabel: "Name",
      roleEn: "Role (EN)",
      roleFr: "Role (FR)",
      bioEn: "Bio (EN)",
      bioFr: "Bio (FR)",
      topicEn: "Topic (EN)",
      topicFr: "Topic (FR)",
      imageUrl: "Image URL",
      uploadPhoto: "Upload Photo",
      photoUploading: "Uploading...",
      orPasteUrl: "or paste URL",
      noSpeakers: "No speakers added yet.",
      programTitle: "Manage Program",
      programLocked: "Program is LOCKED - users see 'locked' message",
      programUnlocked: "Program is UNLOCKED - users can see the full schedule",
      lockProgram: "Lock Program",
      unlockProgram: "Unlock Program",
      addSession: "Add Session",
      editSession: "Edit Session",
      saveSession: "Save Session",
      deleteSession: "Delete",
      timeLabel: "Time",
      titleEn: "Title (EN)",
      titleFr: "Title (FR)",
      sessionType: "Type",
      speakerLabel: "Speaker",
      dayLabel: "Day",
      noSessions: "No sessions added yet.",
      manageUsers: "Users",
      usersTitle: "Manage Users & Roles",
      usersSubtitle: "Promote users to admin or manage their roles",
      promoteAdmin: "Make Admin",
      demoteUser: "Make User",
      promoteSubAdmin: "Make Sub-Admin",
      revokeSubAdmin: "Revoke Sub-Admin",
      promotePartner: "Make Partner",
      revokePartner: "Revoke Partner",
      role: "Role",
      noUsers: "No users found.",
      paid: "Paid",
      deleteUser: "Delete",
      restoreUser: "Restore",
      deletedUsers: "Deleted Users",
      activeUsers: "Active Users",
      confirmDelete: "Confirm?",
      noDeletedUsers: "No deleted users.",
      viewHistory: "History",
      userHistoryTitle: "Event Participation History",
      noHistory: "This user has not participated in any event yet.",
      resendInvitation: "Resend",
      resendConfirm: "Resend the invitation letter to this user by email? (PDF will be attached)",
      resendSuccess: "Invitation resent successfully to",
      incomplete: "Incomplete",
      returnIncomplete: "Return Incomplete",
      returnNote: "Note to attendee (what's missing)",
      returnNotePlaceholder: "e.g. Missing passport number, expiry date, and place of birth...",
      returnSubmit: "Return to Attendee",
      invitation: "Invitation",
      invitationPending: "Pending",
      invitationSent: "Sent",
      markInvitationSent: "Approve & Send",
      pendingInvitation: "Pending",
      sentInvitation: "Sent",
      invitationsTitle: "Visa Invitations",
      invitationsSubtitle: "Review draft invitations and approve with director signature",
      noInvitations: "No paid registrations requiring invitations yet.",
      uploadSignature: "Upload Director Signature",
      signatureUploaded: "Signature on file",
      exemptPayment: "Exempt from Payment",
      exempted: "Exempted",
      revokeExemption: "Revoke Exemption",
    },
    fr: {
      title: "Tableau de Bord Admin",
      subtitle: "Gérer les inscriptions à la conférence",
      tabs: { registrations: "Inscriptions", schedules: "Programmes des Participants", documents: "Documents", resources: "Ressources", manageSpeakers: "Conférenciers", manageProgram: "Programme" },
      all: "Tous", pending: "En attente", approved: "Approuves", rejected: "Rejetes",
      search: "Rechercher par nom ou courriel...",
      name: "Nom", email: "Courriel", status: "Statut", date: "Date", actions: "Actions",
      view: "Voir", approve: "Approuver", reject: "Rejeter", logout: "Déconnexion",
      editAction: "Modifier",
      sendReminderAction: "Rappel de paiement",
      reminderSent: "Rappel de paiement envoyé à",
      noRegistrations: "Aucune inscription trouvee",
      registrationDetails: "Détails de l'Inscription", close: "Fermer",
      age: "Age", phone: "Téléphone", city: "Ville", country: "Pays",
      occupation: "Profession", reason: "Raison de participation",
      heardAbout: "Comment avez-vous entendu", specialNeeds: "Besoins speciaux",
      emergencyContact: "Contact d'urgence", emergencyPhone: "Téléphone d'urgence",
      schedulesTitle: "Sélections de Programme des Participants",
      schedulesSubtitle: "Voyez quelles sessions chaque participant a ajoutees a son programme personnel",
      noSchedules: "Aucun participant n'a encore enregistré son programme.",
      sessions: "sessions sélectionnées",
      docTitle: "Envoyer un Document a un Participant",
      docSubtitle: "Envoyez des lettres d'invitation, communications ou tout document personnel",
      selectUser: "Sélectionner un Participant",
      docTitleLabel: "Titre du Document",
      docDesc: "Description / Message",
      uploadFile: "Télécharger le Fichier",
      send: "Envoyer le Document",
      allDocuments: "Tous les Documents Envoyes",
      noDocuments: "Aucun document envoyé.",
      resTitle: "Télécharger une Ressource",
      resSubtitle: "Téléchargez des ressources (guide du programme, etc.) pour tous les participants",
      resTitleLabel: "Titre de la Ressource",
      resDesc: "Description",
      resType: "Type de Ressource",
      uploadResource: "Télécharger la Ressource",
      sentTo: "Envoyé à",
      type: "Type",
      speakersTitle: "Gérer les Conférenciers",
      speakersLocked: "Section conférenciers VERROUILLÉE - les utilisateurs voient un message de verrouillage",
      speakersUnlocked: "Section conférenciers DÉVERROUILLÉE - les utilisateurs peuvent voir tous les conférenciers",
      lockSpeakers: "Verrouiller",
      unlockSpeakers: "Déverrouiller",
      addSpeaker: "Ajouter un Conférencier",
      editSpeaker: "Modifier",
      saveSpeaker: "Enregistrer",
      deleteSpeaker: "Supprimer",
      nameLabel: "Nom",
      roleEn: "Role (EN)",
      roleFr: "Role (FR)",
      bioEn: "Bio (EN)",
      bioFr: "Bio (FR)",
      topicEn: "Sujet (EN)",
      topicFr: "Sujet (FR)",
      imageUrl: "URL de l'image",
      uploadPhoto: "Télécharger Photo",
      photoUploading: "Téléchargement...",
      orPasteUrl: "ou coller l'URL",
      noSpeakers: "Aucun conferencier ajouté.",
      programTitle: "Gérer le Programme",
      programLocked: "Programme VERROUILLÉ - les utilisateurs voient un message de verrouillage",
      programUnlocked: "Programme DÉVERROUILLÉ - les utilisateurs peuvent voir le programme complet",
      lockProgram: "Verrouiller",
      unlockProgram: "Déverrouiller",
      addSession: "Ajouter une Session",
      editSession: "Modifier",
      saveSession: "Enregistrer",
      deleteSession: "Supprimer",
      timeLabel: "Heure",
      titleEn: "Titre (EN)",
      titleFr: "Titre (FR)",
      sessionType: "Type",
      speakerLabel: "Conférencier",
      dayLabel: "Jour",
      noSessions: "Aucune session ajoutée.",
      manageUsers: "Utilisateurs",
      usersTitle: "Gérer les Utilisateurs et Roles",
      usersSubtitle: "Promouvoir des utilisateurs en administrateur ou gérer leurs rôles",
      promoteAdmin: "Nommer Admin",
      demoteUser: "Retirer Admin",
      promoteSubAdmin: "Nommer Sous-Admin",
      revokeSubAdmin: "Retirer Sous-Admin",
      promotePartner: "Nommer Partenaire",
      revokePartner: "Retirer Partenaire",
      role: "Role",
      noUsers: "Aucun utilisateur trouvé.",
      paid: "Payé",
      deleteUser: "Supprimer",
      restoreUser: "Restaurer",
      deletedUsers: "Utilisateurs Supprimés",
      activeUsers: "Utilisateurs Actifs",
      confirmDelete: "Confirmer?",
      noDeletedUsers: "Aucun utilisateur supprimé.",
      viewHistory: "Historique",
      userHistoryTitle: "Historique de Participation aux Événements",
      noHistory: "Cet utilisateur n'a encore participé à aucun événement.",
      resendInvitation: "Renvoyer",
      resendConfirm: "Renvoyer la lettre d'invitation à cet utilisateur par courriel ? (PDF en pièce jointe)",
      resendSuccess: "Invitation renvoyée avec succès à",
      incomplete: "Incomplète",
      returnIncomplete: "Retourner Incomplète",
      returnNote: "Note au participant (ce qui manque)",
      returnNotePlaceholder: "ex. Numéro de passeport, date d'expiration et lieu de naissance manquants...",
      returnSubmit: "Retourner au Participant",
      invitation: "Invitation",
      invitationPending: "En attente",
      invitationSent: "Envoyée",
      markInvitationSent: "Approuver et Envoyer",
      pendingInvitation: "En attente",
      sentInvitation: "Envoyée",
      invitationsTitle: "Invitations visa",
      invitationsSubtitle: "Réviser les invitations brouillon et approuver avec la signature de la directrice",
      noInvitations: "Aucune inscription payée nécessitant d'invitation pour le moment.",
      uploadSignature: "Télécharger Signature Directrice",
      signatureUploaded: "Signature enregistrée",
      exemptPayment: "Exempter du Paiement",
      exempted: "Exempté",
      revokeExemption: "Retirer l'Exemption",
    }
  };

  const t = content[language] || content.fr;

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== 'admin' && user.role !== 'sub_admin')) { navigate('/login'); return; }
    fetchRegistrations();
    fetchStats();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (activeTab === 'schedules') fetchSchedules();
    if (activeTab === 'documents') fetchDocuments();
    if (activeTab === 'manageSpeakers') { fetchSettings(); fetchAdminSpeakers(); }
    if (activeTab === 'manageProgram') { fetchSettings(); fetchAdminSessions(); }
    if (activeTab === 'manageUsers') fetchAllUsers();
    if (activeTab === 'partnerPayouts') fetchPartnerPayouts();
  }, [activeTab]);

  const fetchPartnerPayouts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/partner-payouts`, { headers: { Authorization: `Bearer ${token}` } });
      setPartnerPayouts(response.data || []);
    } catch (err) {}
  };

  const handleTogglePayoutStatus = async (payout) => {
    const next = payout.status === 'paid' ? 'mark-pending' : 'mark-paid';
    if (!window.confirm(payout.status === 'paid'
      ? (language === 'fr' ? 'Annuler le paiement de cette commission ?' : 'Revert this commission to pending?')
      : (language === 'fr' ? 'Marquer cette commission comme payée ?' : 'Mark this commission as paid?'))) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/partner-payouts/${payout.id}/${next}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchPartnerPayouts();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update payout');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setServerStats(response.data);
    } catch (err) {}
  };

  const fetchRegistrations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/registrations`, { headers: { Authorization: `Bearer ${token}` } });
      setRegistrations(response.data);
    } catch (err) {} finally { setLoading(false); }
  };

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/schedules`, { headers: { Authorization: `Bearer ${token}` } });
      setSchedules(response.data);
    } catch (err) {}
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/documents`, { headers: { Authorization: `Bearer ${token}` } });
      setDocuments(response.data);
    } catch (err) {}
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(response.data);
    } catch (err) {}
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/deleted`, { headers: { Authorization: `Bearer ${token}` } });
      setDeletedUsers(response.data);
    } catch (err) {}
  };

  const handleRoleUpdate = async (userId, newRole) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/users/${userId}/role`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAllUsers();
    } catch (err) {}
  };

  const handleDeleteUser = async (userId, email) => {
    if (confirmingDelete !== userId) {
      setConfirmingDelete(userId);
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setConfirmingDelete(null);
      fetchAllUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting user');
      setConfirmingDelete(null);
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/users/${userId}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchAllUsers();
    } catch (err) {}
  };

  const handleViewUserHistory = async (userId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}/participations`, { headers: { Authorization: `Bearer ${token}` } });
      setViewingUserHistory(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error loading history');
    }
  };

  const handleImpersonate = async (targetUser) => {
    const label = language === 'fr'
      ? `Vous allez ouvrir une session en tant que ${targetUser.first_name} ${targetUser.last_name}.\n\nVotre session admin sera mise en pause et restaurée lorsque vous cliquerez sur « Stop impersonating » dans la bannière en haut.\n\nContinuer ?`
      : `You are about to open a session as ${targetUser.first_name} ${targetUser.last_name}.\n\nYour admin session will be parked and restored when you click "Stop impersonating" in the banner at the top.\n\nContinue?`;
    if (!window.confirm(label)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${targetUser.id}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      startImpersonation({
        token: res.data.token,
        user: res.data.user,
        admin: { id: user.id, first_name: user.first_name, last_name: user.last_name, email: user.email },
      });
      // Hard navigation to avoid AdminDashboard's role-check effect bouncing us to /login
      window.location.href = '/dashboard';
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start impersonation');
    }
  };

  const handlePreviewSample = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/invitations/preview-sample`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load preview');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      alert(language === 'fr' ? 'Erreur lors du chargement de l\'aperçu' : 'Error loading preview');
    }
  };

  const handleDispatchCertificates = async () => {
    const msg = language === 'fr'
      ? "Envoyer les certificats de participation par courriel à tous les participants payés des événements terminés ? Cette action est idempotente — les certificats déjà envoyés ne seront pas réexpédiés."
      : "Send attendance certificates by email to all paid attendees of past events? This action is idempotent — already-sent certificates will not be re-sent.";
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/certificates/dispatch-pending`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data;
      const summary = language === 'fr'
        ? `Envoyés : ${d.sent} · Ignorés : ${d.skipped} · Erreurs : ${d.errors || 0}`
        : `Sent: ${d.sent} · Skipped: ${d.skipped} · Errors: ${d.errors || 0}`;
      alert(summary);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const handleResendInvitation = async (registrationId, userEmail) => {
    if (!window.confirm(t.resendConfirm)) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/registrations/${registrationId}/invitation/resend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert(`${t.resendSuccess} ${res.data.email || userEmail}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error resending invitation');
    }
  };

  const handleInvitationUpdate = async (registrationId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/registrations/${registrationId}/invitation`, { invitation_status: status }, { headers: { Authorization: `Bearer ${token}` } });
      fetchRegistrations();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating invitation');
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/signature/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      alert(t.signatureUploaded);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const handleReturnIncomplete = async (registrationId) => {
    if (!incompleteNote.trim()) return;
    try {
      await axios.patch(`${BACKEND_URL}/api/registrations/${registrationId}/return-incomplete`, { note: incompleteNote }, { headers: { Authorization: `Bearer ${token}` } });
      setIncompleteNote('');
      setShowReturnForm(false);
      setSelectedRegistration(null);
      fetchRegistrations();
    } catch (err) {}
  };

  const handleExemptPayment = async (registrationId, userName) => {
    const msg = language === 'fr'
      ? `Exempter ${userName} du paiement et lui accorder un accès gratuit à la conférence ?\n\nCette action :\n• Débloque immédiatement le contenu de la conférence\n• Génère automatiquement la lettre d'invitation visa\n• Envoie un email de confirmation au participant\n\nContinuer ?`
      : `Exempt ${userName} from payment and grant complimentary conference access?\n\nThis will:\n• Immediately unlock conference content\n• Auto-generate the visa invitation letter\n• Send a confirmation email to the participant\n\nContinue?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.patch(`${BACKEND_URL}/api/registrations/${registrationId}/exempt-payment`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedRegistration(null);
      fetchRegistrations();
      alert(language === 'fr' ? 'Participant exempté du paiement avec succès.' : 'Participant successfully exempted from payment.');
    } catch (err) {
      alert(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'exemption' : 'Error exempting payment'));
    }
  };

  const handleRevokeExemption = async (reg) => {
    const userName = `${reg.user?.first_name || ''} ${reg.user?.last_name || ''}`.trim();
    const msg = language === 'fr'
      ? `Retirer l'exemption de paiement pour ${userName} ?\n\nCette action :\n• Remet le statut à « Approuvée » (non payée)\n• Verrouille à nouveau le contenu de la conférence\n• Réactive les rappels de paiement automatiques\n\nContinuer ?`
      : `Revoke payment exemption for ${userName}?\n\nThis will:\n• Revert status to "Approved" (unpaid)\n• Re-lock conference content\n• Re-enable automatic payment reminders\n\nContinue?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.patch(`${BACKEND_URL}/api/registrations/${reg.id}/revoke-exemption`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchRegistrations();
      alert(language === 'fr' ? "Exemption retirée avec succès." : "Exemption revoked successfully.");
    } catch (err) {
      alert(err.response?.data?.detail || (language === 'fr' ? "Erreur lors du retrait de l'exemption" : 'Error revoking exemption'));
    }
  };


  const handleSendPaymentReminder = async (reg) => {
    const userName = `${reg.user?.first_name || ''} ${reg.user?.last_name || ''}`.trim();
    const confirmMsg = language === 'fr'
      ? `Envoyer un rappel de paiement à ${userName} (${reg.user?.email}) ?`
      : `Send a payment reminder to ${userName} (${reg.user?.email})?`;
    if (!window.confirm(confirmMsg)) return;
    setSendingReminderId(reg.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/registrations/${reg.id}/payment-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`${t.reminderSent} ${reg.user?.email}`);
    } catch (err) {
      alert(err?.response?.data?.detail || (language === 'fr' ? "Erreur lors de l'envoi du rappel" : 'Error sending reminder'));
    } finally {
      setSendingReminderId(null);
    }
  };

  const handleDispatchAutoReminders = async () => {
    const confirmMsg = language === 'fr'
      ? 'Dispatcher maintenant le lot automatique de rappels de paiement (règle 72h / 7 jours) ?'
      : 'Run the automatic payment-reminder batch now (72h / 7d rule)?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/payment-reminders/dispatch-pending`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sent = 0, skipped = 0, errors = 0 } = res.data || {};
      alert(language === 'fr'
        ? `Lot terminé. Envoyés: ${sent}, Ignorés: ${skipped}, Erreurs: ${errors}.`
        : `Batch complete. Sent: ${sent}, Skipped: ${skipped}, Errors: ${errors}.`);
    } catch (err) {
      alert(err?.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const handleSendOttawaTeaser = async () => {
    const choice = window.prompt(
      language === 'fr'
        ? "Audience de la campagne « Saviez-vous ? » Ottawa :\n\n1 — Tous les utilisateurs\n2 — Uniquement les non payés (avec rappel de paiement)\n\nTapez 1 ou 2 :"
        : "'Did You Know?' Ottawa campaign audience:\n\n1 — All users\n2 — Only unpaid users (with payment reminder)\n\nType 1 or 2:",
      "1"
    );
    if (!choice) return;
    const audience = choice.trim() === "2" ? "unpaid" : "all";
    const confirmMsg = language === 'fr'
      ? `Envoyer maintenant le email « Saviez-vous ? » à l'audience « ${audience === 'unpaid' ? 'non payés' : 'tous'} » ?`
      : `Send the 'Did You Know?' email now to audience '${audience}'?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/campaigns/ottawa-teaser`,
        { audience },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sent = 0, skipped = 0, errors = 0, days_to_go = 0 } = res.data || {};
      alert(language === 'fr'
        ? `Campagne terminée (J-${days_to_go}). Envoyés: ${sent}, Ignorés: ${skipped}, Erreurs: ${errors}.`
        : `Campaign complete (${days_to_go} days to go). Sent: ${sent}, Skipped: ${skipped}, Errors: ${errors}.`);
    } catch (err) {
      alert(err?.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const handleAdminDownloadProgram = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/program/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'TALITHA-KOUMI-TUG-Programme-Mars-2027-Master.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSettings(response.data);
    } catch (err) {}
  };

  const fetchAdminSpeakers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/speakers`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminSpeakers(response.data);
    } catch (err) {}
  };

  const fetchAdminSessions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminSessions(response.data);
    } catch (err) {}
  };

  const toggleLock = async (field, currentValue) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/settings`, { [field]: !currentValue }, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(prev => ({ ...prev, [field]: !currentValue }));
    } catch (err) {}
  };

  const handleSaveSpeaker = async (e) => {
    e.preventDefault();
    try {
      if (editingSpeaker) {
        await axios.patch(`${BACKEND_URL}/api/admin/speakers/${editingSpeaker}`, speakerForm, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/speakers`, speakerForm, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSpeakerForm({ name: '', role_en: '', role_fr: '', bio_en: '', bio_fr: '', topic_en: '', topic_fr: '', image: '' });
      setEditingSpeaker(null);
      fetchAdminSpeakers();
    } catch (err) {}
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/speakers/upload-photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setSpeakerForm(p => ({ ...p, image: imageUrl }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Photo upload failed');
    }
    finally { setPhotoUploading(false); e.target.value = ''; }
  };

  const handleDeleteSpeaker = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/speakers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAdminSpeakers();
    } catch (err) {}
  };

  const startEditSpeaker = (speaker) => {
    setEditingSpeaker(speaker.id);
    setSpeakerForm({
      name: speaker.name, role_en: speaker.role?.en || '', role_fr: speaker.role?.fr || '',
      bio_en: speaker.bio?.en || '', bio_fr: speaker.bio?.fr || '',
      topic_en: speaker.topic?.en || '', topic_fr: speaker.topic?.fr || '',
      image: speaker.image || ''
    });
  };

  const handleSaveSession = async (e) => {
    e.preventDefault();
    try {
      if (editingSession) {
        await axios.patch(`${BACKEND_URL}/api/admin/sessions/${editingSession}`, sessionForm, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/sessions`, sessionForm, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSessionForm({ time: '', title_en: '', title_fr: '', session_type: 'session', speaker: '', day: 1 });
      setEditingSession(null);
      fetchAdminSessions();
    } catch (err) {}
  };

  const handleDeleteSession = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/sessions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAdminSessions();
    } catch (err) {}
  };

  const startEditSession = (session) => {
    setEditingSession(session.id);
    setSessionForm({
      time: session.time, title_en: session.title?.en || '', title_fr: session.title?.fr || '',
      session_type: session.session_type || 'session', speaker: session.speaker || '', day: session.day || 1
    });
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/registrations/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      fetchRegistrations();
      setSelectedRegistration(null);
    } catch (err) {}
  };

  const handleDocUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.targetUserId || !uploadForm.title) return;
    setUploading(true);
    setUploadMsg('');
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('target_user_id', uploadForm.targetUserId);
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/documents/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setUploadMsg('Document sent successfully!');
      setUploadForm({ targetUserId: '', title: '', description: '', file: null });
      fetchDocuments();
    } catch (err) { setUploadMsg('Upload failed. Try again.'); }
    finally { setUploading(false); }
  };

  const handleResourceUpload = async (e) => {
    e.preventDefault();
    if (!resourceForm.file || !resourceForm.title) return;
    setUploading(true);
    setUploadMsg('');
    const formData = new FormData();
    formData.append('file', resourceForm.file);
    formData.append('title', resourceForm.title);
    formData.append('description', resourceForm.description);
    formData.append('resource_type', resourceForm.resourceType);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/resources/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setUploadMsg('Resource uploaded successfully!');
      setResourceForm({ title: '', description: '', resourceType: 'general', file: null });
      fetchDocuments();
    } catch (err) { setUploadMsg('Upload failed. Try again.'); }
    finally { setUploading(false); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesFilter = filter === 'all' || reg.status === filter;
    const matchesSearch =
      reg.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const isSubAdmin = user?.role === 'sub_admin';

  const stats = serverStats ? {
    total: serverStats.total || 0,
    pending: serverStats.pending || 0,
    approved: serverStats.approved || 0,
    rejected: serverStats.rejected || 0,
    paid: serverStats.paid || 0,
    exempted: serverStats.exempted || 0,
    incomplete: serverStats.incomplete || 0,
    revenue: serverStats.revenue || 0,
  } : {
    total: registrations.length,
    pending: registrations.filter(r => r.status === 'pending').length,
    approved: registrations.filter(r => r.status === 'approved').length,
    rejected: registrations.filter(r => r.status === 'rejected').length,
    // "Paid" = real paying customers only, exempted are counted separately
    paid: registrations.filter(r => r.status === 'paid' && !r.payment_exempted).length,
    exempted: registrations.filter(r => r.status === 'paid' && r.payment_exempted).length,
    incomplete: registrations.filter(r => r.status === 'incomplete').length,
  };

  const approvedUsers = registrations.filter(r => r.status === 'approved' || r.status === 'pending').map(r => r.user).filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CD0000]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7F9] flex">
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
        language={language}
        stats={stats}
        onLogout={handleLogout}
        role={user?.role}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        <AdminTopBar
          activeTab={activeTab}
          language={language}
          user={user}
          onLanguageToggle={toggleLanguage}
        />

        <main className="flex-1 p-5 md:p-7 max-w-[1400px] w-full mx-auto" data-testid="admin-main-content">

        {/* ========== OVERVIEW TAB ========== */}
        {activeTab === 'overview' && (
          <AdminOverview
            language={language}
            user={user}
            stats={stats}
            registrations={registrations}
            invitationsPendingCount={isSubAdmin ? 0 : (registrations || []).filter(r => r.status === 'paid' && r.invitation_status !== 'sent').length}
            onNavigate={setActiveTab}
            onDispatchReminders={isSubAdmin ? null : handleDispatchAutoReminders}
            onSendOttawaTeaser={isSubAdmin ? null : handleSendOttawaTeaser}
            onOpenPostponement={isSubAdmin ? null : () => setShowPostponement(true)}
            onDownloadProgram={handleAdminDownloadProgram}
            isSubAdmin={isSubAdmin}
          />
        )}

        {/* ========== QR CHECK-IN TAB ========== */}
        {activeTab === 'checkin' && (
          <AdminCheckin language={language} token={token} />
        )}

        {/* ========== REGISTRATIONS TAB ========== */}
        {activeTab === 'registrations' && (
          <>
            <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select data-testid="admin-filter-select" value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#D4AF37]">
                  <option value="all">{t.all}</option>
                  <option value="pending">{t.pending}</option>
                  <option value="approved">{t.approved}</option>
                  {!isSubAdmin && <option value="paid">{t.paid}</option>}
                  <option value="incomplete">{t.incomplete}</option>
                  <option value="rejected">{t.rejected}</option>
                </select>
              </div>
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input data-testid="admin-search-input" type="text" placeholder={t.search} value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D4AF37]" />
              </div>
            </div>

            <div data-testid="admin-registrations-table" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.name}</th>
                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.email}</th>
                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.status}</th>
                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.date}</th>
                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRegistrations.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400 text-sm">{t.noRegistrations}</td></tr>
                  ) : (
                    filteredRegistrations.map((reg) => (
                      <tr key={reg.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm font-medium">{reg.user?.first_name} {reg.user?.last_name}</td>
                        <td className="px-5 py-3 text-sm text-gray-500">{reg.user?.email}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              reg.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                              reg.status === 'approved' ? 'bg-green-100 text-green-800' :
                              reg.status === 'incomplete' ? 'bg-orange-100 text-orange-800' :
                              reg.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {reg.status === 'paid' ? t.paid :
                               reg.status === 'approved' ? t.approved :
                               reg.status === 'incomplete' ? t.incomplete :
                               reg.status === 'rejected' ? t.rejected : t.pending}
                            </span>
                            {reg.status === 'paid' && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                reg.invitation_status === 'sent' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                              }`}>
                                {reg.invitation_status === 'sent' ? t.invitationSent : t.pendingInvitation}
                              </span>
                            )}
                            {reg.payment_exempted && (
                              <span data-testid={`exempted-badge-${reg.id}`} className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                {t.exempted}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-500">{new Date(reg.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-3">
                          <div className="flex gap-2 flex-wrap">
                            <button onClick={() => setSelectedRegistration(reg)} data-testid={`admin-view-btn-${reg.id}`}
                              className="text-blue-600 hover:underline flex items-center gap-1 text-sm">
                              <Eye className="w-3.5 h-3.5" /> {t.view}
                            </button>
                            {!isSubAdmin && (
                              <button onClick={() => setEditingRegistration(reg)} data-testid={`admin-edit-reg-btn-${reg.id}`}
                                className="text-[#CD0000] hover:underline flex items-center gap-1 text-sm">
                                <Edit3 className="w-3.5 h-3.5" /> {t.editAction}
                              </button>
                            )}
                            {reg.status === 'pending' && (
                              <>
                                <button onClick={() => handleStatusUpdate(reg.id, 'approved')} data-testid={`admin-approve-btn-${reg.id}`}
                                  className="text-green-600 hover:underline text-sm">{t.approve}</button>
                                <button onClick={() => handleStatusUpdate(reg.id, 'rejected')} data-testid={`admin-reject-btn-${reg.id}`}
                                  className="text-red-600 hover:underline text-sm">{t.reject}</button>
                              </>
                            )}
                            {reg.status === 'approved' && (
                              <>
                                <button onClick={() => handleSendPaymentReminder(reg)} disabled={sendingReminderId === reg.id} data-testid={`admin-payment-reminder-btn-${reg.id}`}
                                  className="text-orange-600 hover:underline text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
                                  <BellRing className="w-3.5 h-3.5" /> {t.sendReminderAction}
                                </button>
                                {!isSubAdmin && (
                                  <button onClick={() => handleExemptPayment(reg.id, `${reg.user?.first_name} ${reg.user?.last_name}`)} data-testid={`admin-exempt-btn-${reg.id}`}
                                    className="text-purple-600 hover:underline text-sm font-semibold">{t.exemptPayment}</button>
                                )}
                              </>
                            )}
                            {!isSubAdmin && reg.payment_exempted && reg.status === 'paid' && (
                              <button onClick={() => handleRevokeExemption(reg)} data-testid={`admin-revoke-exemption-btn-${reg.id}`}
                                className="text-rose-600 hover:underline text-sm font-semibold flex items-center gap-1">
                                <RotateCcw className="w-3.5 h-3.5" /> {t.revokeExemption}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ========== SCHEDULES TAB ========== */}
        {activeTab === 'schedules' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-extrabold text-[#1D1D1D]">{t.schedulesTitle}</h2>
              <p className="text-gray-500 text-sm mt-1">{t.schedulesSubtitle}</p>
            </div>
            {schedules.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t.noSchedules}</p>
              </div>
            ) : (
              schedules.map((s, i) => (
                <div key={i} data-testid={`admin-schedule-${i}`} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm">{s.user?.first_name} {s.user?.last_name}</h4>
                        <p className="text-gray-400 text-xs">{s.user?.email}</p>
                      </div>
                    </div>
                    <span className="bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-bold px-3 py-1 rounded-full">
                      {s.sessions?.length || 0} {t.sessions}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(s.sessions || []).map(sid => (
                      <span key={sid} className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-lg">
                        {scheduleSessionNames[sid] || sid}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ========== DOCUMENTS TAB ========== */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            {/* Upload Form */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-extrabold text-[#1D1D1D] mb-1">{t.docTitle}</h3>
              <p className="text-gray-500 text-sm mb-4">{t.docSubtitle}</p>
              <form onSubmit={handleDocUpload} className="space-y-4" data-testid="admin-doc-upload-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.selectUser}</label>
                  <select value={uploadForm.targetUserId} onChange={e => setUploadForm(p => ({...p, targetUserId: e.target.value}))}
                    data-testid="admin-doc-user-select" required
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]">
                    <option value="">{t.selectUser}...</option>
                    {approvedUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.docTitleLabel}</label>
                  <input type="text" value={uploadForm.title} onChange={e => setUploadForm(p => ({...p, title: e.target.value}))}
                    data-testid="admin-doc-title-input" required placeholder="e.g. Visa Invitation Letter"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.docDesc}</label>
                  <textarea value={uploadForm.description} onChange={e => setUploadForm(p => ({...p, description: e.target.value}))}
                    data-testid="admin-doc-desc-input" rows={2} placeholder="Optional message for the attendee"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.uploadFile}</label>
                  <input type="file" onChange={e => setUploadForm(p => ({...p, file: e.target.files[0]}))}
                    data-testid="admin-doc-file-input" required accept=".pdf,.doc,.docx,.txt,.png,.jpg"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-[#CD0000]/10 file:text-[#CD0000] file:font-bold" />
                </div>
                {uploadMsg && <p className={`text-sm font-medium ${uploadMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{uploadMsg}</p>}
                <button type="submit" disabled={uploading} data-testid="admin-doc-submit"
                  className="bg-[#CD0000] text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-[#c91560] disabled:opacity-50 transition-colors flex items-center gap-2">
                  <Upload className="w-4 h-4" /> {uploading ? '...' : t.send}
                </button>
              </form>
            </div>

            {/* Sent Documents List */}
            <div>
              <h3 className="font-extrabold text-[#1D1D1D] mb-3">{t.allDocuments}</h3>
              {documents.filter(d => d.doc_type === 'personal').length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">{t.noDocuments}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.filter(d => d.doc_type === 'personal').map((doc, i) => (
                    <div key={doc.id} data-testid={`admin-sent-doc-${i}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-sm">{doc.title}</h4>
                          <p className="text-gray-400 text-xs">
                            {t.sentTo}: {doc.target_user?.first_name} {doc.target_user?.last_name} | {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className="text-gray-400 text-xs">{doc.original_filename}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== RESOURCES TAB ========== */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-extrabold text-[#1D1D1D] mb-1">{t.resTitle}</h3>
              <p className="text-gray-500 text-sm mb-4">{t.resSubtitle}</p>
              <form onSubmit={handleResourceUpload} className="space-y-4" data-testid="admin-resource-upload-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.resTitleLabel}</label>
                  <input type="text" value={resourceForm.title} onChange={e => setResourceForm(p => ({...p, title: e.target.value}))}
                    data-testid="admin-res-title-input" required placeholder="e.g. Conference Program Guide"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.resDesc}</label>
                  <textarea value={resourceForm.description} onChange={e => setResourceForm(p => ({...p, description: e.target.value}))}
                    data-testid="admin-res-desc-input" rows={2}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.resType}</label>
                  <select value={resourceForm.resourceType} onChange={e => setResourceForm(p => ({...p, resourceType: e.target.value}))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]">
                    <option value="general">General</option>
                    <option value="program">Program Guide</option>
                    <option value="preparation">Preparation Kit</option>
                    <option value="safety">Health & Safety</option>
                    <option value="city">City Guide</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.uploadFile}</label>
                  <input type="file" onChange={e => setResourceForm(p => ({...p, file: e.target.files[0]}))}
                    data-testid="admin-res-file-input" required accept=".pdf,.doc,.docx,.txt,.png,.jpg"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-[#D4AF37]/10 file:text-[#D4AF37] file:font-bold" />
                </div>
                {uploadMsg && <p className={`text-sm font-medium ${uploadMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{uploadMsg}</p>}
                <button type="submit" disabled={uploading} data-testid="admin-res-submit"
                  className="bg-[#D4AF37] text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-[#4d8a29] disabled:opacity-50 transition-colors flex items-center gap-2">
                  <Upload className="w-4 h-4" /> {uploading ? '...' : t.uploadResource}
                </button>
              </form>
            </div>

            {/* Uploaded Resources List */}
            {documents.filter(d => d.doc_type === 'resource').length > 0 && (
              <div className="space-y-3">
                {documents.filter(d => d.doc_type === 'resource').map((res, i) => (
                  <div key={res.id} data-testid={`admin-resource-${i}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm">{res.title}</h4>
                        <p className="text-gray-400 text-xs">{res.original_filename} | {new Date(res.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className="bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-bold px-3 py-1 rounded-full">{res.resource_type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== MANAGE SPEAKERS TAB ========== */}
        {activeTab === 'manageSpeakers' && (
          <div className="space-y-6">
            {/* Lock Toggle */}
            <div className={`rounded-2xl p-4 flex items-center justify-between ${settings.speakers_locked ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center gap-3">
                {settings.speakers_locked ? <Lock className="w-5 h-5 text-red-600" /> : <Unlock className="w-5 h-5 text-green-600" />}
                <p className={`text-sm font-bold ${settings.speakers_locked ? 'text-red-700' : 'text-green-700'}`}>
                  {settings.speakers_locked ? t.speakersLocked : t.speakersUnlocked}
                </p>
              </div>
              <button onClick={() => toggleLock('speakers_locked', settings.speakers_locked)} data-testid="toggle-speakers-lock"
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${settings.speakers_locked ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                {settings.speakers_locked ? <><Unlock className="w-3.5 h-3.5 inline mr-1" />{t.unlockSpeakers}</> : <><Lock className="w-3.5 h-3.5 inline mr-1" />{t.lockSpeakers}</>}
              </button>
            </div>

            {/* Add/Edit Speaker Form */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-extrabold text-[#1D1D1D] mb-4">{editingSpeaker ? t.editSpeaker : t.addSpeaker}</h3>
              <form onSubmit={handleSaveSpeaker} className="space-y-3" data-testid="speaker-form">
                <div className="grid md:grid-cols-2 gap-3">
                  <input type="text" placeholder={t.nameLabel} value={speakerForm.name} onChange={e => setSpeakerForm(p => ({...p, name: e.target.value}))} data-testid="speaker-name-input" required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input type="text" placeholder={`${t.imageUrl} ${t.orPasteUrl}`} value={speakerForm.image} onChange={e => setSpeakerForm(p => ({...p, image: e.target.value}))} data-testid="speaker-image-input" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37] pr-24" />
                    </div>
                    <label data-testid="speaker-photo-upload-btn" className={`shrink-0 px-3 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-colors flex items-center gap-1.5 ${photoUploading ? 'bg-gray-200 text-gray-500' : 'bg-[#CD0000]/10 text-[#CD0000] hover:bg-[#CD0000]/20'}`}>
                      <Image className="w-4 h-4" />
                      {photoUploading ? t.photoUploading : t.uploadPhoto}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handlePhotoUpload} disabled={photoUploading} className="hidden" />
                    </label>
                    {speakerForm.image && (
                      <img src={speakerForm.image} alt="Preview" className="w-10 h-10 rounded-lg object-cover shrink-0 border border-gray-200" onError={e => e.target.style.display='none'} />
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input type="text" placeholder={t.roleEn} value={speakerForm.role_en} onChange={e => setSpeakerForm(p => ({...p, role_en: e.target.value}))} required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                  <input type="text" placeholder={t.roleFr} value={speakerForm.role_fr} onChange={e => setSpeakerForm(p => ({...p, role_fr: e.target.value}))} required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input type="text" placeholder={t.topicEn} value={speakerForm.topic_en} onChange={e => setSpeakerForm(p => ({...p, topic_en: e.target.value}))} required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                  <input type="text" placeholder={t.topicFr} value={speakerForm.topic_fr} onChange={e => setSpeakerForm(p => ({...p, topic_fr: e.target.value}))} required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <textarea placeholder={t.bioEn} value={speakerForm.bio_en} onChange={e => setSpeakerForm(p => ({...p, bio_en: e.target.value}))} required rows={2} className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                  <textarea placeholder={t.bioFr} value={speakerForm.bio_fr} onChange={e => setSpeakerForm(p => ({...p, bio_fr: e.target.value}))} required rows={2} className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <div className="flex gap-2">
                  <button type="submit" data-testid="speaker-save-btn" className="bg-[#CD0000] text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#c91560] transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" /> {editingSpeaker ? t.saveSpeaker : t.addSpeaker}
                  </button>
                  {editingSpeaker && (
                    <button type="button" onClick={() => { setEditingSpeaker(null); setSpeakerForm({ name: '', role_en: '', role_fr: '', bio_en: '', bio_fr: '', topic_en: '', topic_fr: '', image: '' }); }}
                      className="bg-gray-200 text-gray-700 px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-300 transition-colors">Cancel</button>
                  )}
                </div>
              </form>
            </div>

            {/* Speaker List */}
            {adminSpeakers.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                <Mic2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">{t.noSpeakers}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {adminSpeakers.map((speaker, i) => (
                  <div key={speaker.id} data-testid={`admin-speaker-${i}`} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {speaker.image && <img src={speaker.image} alt={speaker.name} className="w-12 h-12 rounded-xl object-cover" />}
                        <div>
                          <h4 className="font-bold text-sm">{speaker.name}</h4>
                          <p className="text-gray-500 text-xs">{speaker.role?.en} | {speaker.topic?.en}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => startEditSpeaker(speaker)} data-testid={`edit-speaker-${speaker.id}`} className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteSpeaker(speaker.id)} data-testid={`delete-speaker-${speaker.id}`} className="text-red-600 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== MANAGE PROGRAM TAB ========== */}
        {activeTab === 'manageProgram' && (
          <div className="space-y-6">
            {/* Lock Toggle */}
            <div className={`rounded-2xl p-4 flex items-center justify-between ${settings.program_locked ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center gap-3">
                {settings.program_locked ? <Lock className="w-5 h-5 text-red-600" /> : <Unlock className="w-5 h-5 text-green-600" />}
                <p className={`text-sm font-bold ${settings.program_locked ? 'text-red-700' : 'text-green-700'}`}>
                  {settings.program_locked ? t.programLocked : t.programUnlocked}
                </p>
              </div>
              <button onClick={() => toggleLock('program_locked', settings.program_locked)} data-testid="toggle-program-lock"
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${settings.program_locked ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                {settings.program_locked ? <><Unlock className="w-3.5 h-3.5 inline mr-1" />{t.unlockProgram}</> : <><Lock className="w-3.5 h-3.5 inline mr-1" />{t.lockProgram}</>}
              </button>
            </div>

            {/* Add/Edit Session Form */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-extrabold text-[#1D1D1D] mb-4">{editingSession ? t.editSession : t.addSession}</h3>
              <form onSubmit={handleSaveSession} className="space-y-3" data-testid="session-form">
                <div className="grid md:grid-cols-3 gap-3">
                  <input type="text" placeholder={`${t.timeLabel} (e.g. 09:00 - 10:30)`} value={sessionForm.time} onChange={e => setSessionForm(p => ({...p, time: e.target.value}))} data-testid="session-time-input" required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                  <select value={sessionForm.day} onChange={e => setSessionForm(p => ({...p, day: parseInt(e.target.value)}))} data-testid="session-day-select" className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]">
                    <option value={1}>{t.dayLabel} 1 - Mar 12</option>
                    <option value={2}>{t.dayLabel} 2 - Mar 13</option>
                    <option value={3}>{t.dayLabel} 3 - Mar 14</option>
                  </select>
                  <select value={sessionForm.session_type} onChange={e => setSessionForm(p => ({...p, session_type: e.target.value}))} data-testid="session-type-select" className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]">
                    <option value="keynote">Keynote</option><option value="panel">Panel</option><option value="workshop">Workshop</option>
                    <option value="session">Session</option><option value="general">General</option><option value="social">Social</option><option value="wellness">Wellness</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input type="text" placeholder={t.titleEn} value={sessionForm.title_en} onChange={e => setSessionForm(p => ({...p, title_en: e.target.value}))} data-testid="session-title-en-input" required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                  <input type="text" placeholder={t.titleFr} value={sessionForm.title_fr} onChange={e => setSessionForm(p => ({...p, title_fr: e.target.value}))} data-testid="session-title-fr-input" required className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                </div>
                <input type="text" placeholder={t.speakerLabel} value={sessionForm.speaker} onChange={e => setSessionForm(p => ({...p, speaker: e.target.value}))} data-testid="session-speaker-input" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#D4AF37]" />
                <div className="flex gap-2">
                  <button type="submit" data-testid="session-save-btn" className="bg-[#D4AF37] text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#4d8a29] transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" /> {editingSession ? t.saveSession : t.addSession}
                  </button>
                  {editingSession && (
                    <button type="button" onClick={() => { setEditingSession(null); setSessionForm({ time: '', title_en: '', title_fr: '', session_type: 'session', speaker: '', day: 1 }); }}
                      className="bg-gray-200 text-gray-700 px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-300 transition-colors">Cancel</button>
                  )}
                </div>
              </form>
            </div>

            {/* Session List */}
            {adminSessions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">{t.noSessions}</p>
              </div>
            ) : (
              [1, 2, 3].map(day => {
                const daySessions = adminSessions.filter(s => s.day === day);
                if (daySessions.length === 0) return null;
                return (
                  <div key={day}>
                    <h4 className="font-bold text-[#1D1D1D] mb-2">{t.dayLabel} {day} - Mar {day + 11}</h4>
                    <div className="space-y-2">
                      {daySessions.map((session) => (
                        <div key={session.id} data-testid={`admin-session-${session.id}`} className="bg-white rounded-2xl border border-gray-100 p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold text-gray-400 w-28">{session.time}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{session.session_type}</span>
                              <div>
                                <h4 className="font-bold text-sm">{session.title?.en}</h4>
                                <p className="text-gray-400 text-xs">{session.title?.fr}{session.speaker ? ` | ${session.speaker}` : ''}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => startEditSession(session)} data-testid={`edit-session-${session.id}`} className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"><Edit className="w-4 h-4" /></button>
                              <button onClick={() => handleDeleteSession(session.id)} data-testid={`delete-session-${session.id}`} className="text-red-600 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        {/* ========== MANAGE USERS TAB ========== */}
        {activeTab === 'manageUsers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-[#1D1D1D]">{t.usersTitle}</h2>
                <p className="text-gray-500 text-sm mt-1">{t.usersSubtitle}</p>
              </div>
              <div className="flex items-center gap-2">
                {!isSubAdmin && (
                  <button
                    onClick={() => setCreatingPartner(true)}
                    data-testid="create-partner-btn"
                    className="px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 bg-[#D4AF37] hover:bg-amber-500 text-black"
                  >
                    <Sparkles className="w-4 h-4" /> {language === 'fr' ? 'Créer Partenaire' : 'Create Partner'}
                  </button>
                )}
                <button
                  onClick={() => setShowDeletedUsers(!showDeletedUsers)}
                  data-testid="toggle-deleted-users"
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 ${
                    showDeletedUsers ? 'bg-[#1D1D1D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {showDeletedUsers ? <><Users className="w-4 h-4" /> {t.activeUsers}</> : <><UserX className="w-4 h-4" /> {t.deletedUsers} ({deletedUsers.length})</>}
                </button>
              </div>
            </div>

            {!showDeletedUsers ? (
              /* Active Users */
              allUsers.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                  <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">{t.noUsers}</p>
                </div>
              ) : (
                <div data-testid="admin-users-table" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.name}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.email}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.role}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.actions}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {allUsers.map((u, i) => (
                        <tr key={u.id} data-testid={`admin-user-row-${i}`} className="hover:bg-gray-50">
                          <td className="px-5 py-3 text-sm font-medium">{u.first_name} {u.last_name}</td>
                          <td className="px-5 py-3 text-sm text-gray-500">{u.email}</td>
                          <td className="px-5 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                              u.role === 'sub_admin' ? 'bg-amber-100 text-amber-800' :
                              u.role === 'partner' ? 'bg-[#D4AF37]/20 text-[#8a7223] border border-[#D4AF37]/40' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {u.role === 'sub_admin' ? (language === 'fr' ? 'sous-admin' : 'sub-admin') :
                               u.role === 'partner' ? (language === 'fr' ? 'partenaire' : 'partner') : u.role}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={() => handleViewUserHistory(u.id)}
                                data-testid={`view-history-${u.id}`}
                                className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                              >
                                <Award className="w-3.5 h-3.5" /> {t.viewHistory}
                              </button>
                              <button
                                onClick={() => setEditingUser(u)}
                                data-testid={`admin-edit-user-btn-${u.id}`}
                                className="text-[#CD0000] hover:underline text-sm flex items-center gap-1"
                                disabled={isSubAdmin && (u.role === 'admin' || u.role === 'sub_admin')}
                                title={isSubAdmin && (u.role === 'admin' || u.role === 'sub_admin') ? (language === 'fr' ? 'Réservé à l\'administrateur' : 'Admin only') : undefined}
                              >
                                <Edit3 className="w-3.5 h-3.5" /> {t.editAction}
                              </button>
                              {!isSubAdmin && u.email !== user.email && u.role !== 'admin' && (
                                <button
                                  onClick={() => handleImpersonate(u)}
                                  data-testid={`impersonate-user-${u.id}`}
                                  className="text-amber-600 hover:underline text-sm flex items-center gap-1"
                                  title={language === 'fr' ? 'Se connecter en tant que cet utilisateur' : 'Sign in as this user'}
                                >
                                  <LogIn className="w-3.5 h-3.5" />
                                  {language === 'fr' ? 'Connexion en tant que' : 'Login as'}
                                </button>
                              )}
                              {!isSubAdmin && u.email !== user.email && (
                                <>
                                  {u.role === 'user' && (
                                    <>
                                      <button
                                        onClick={() => handleRoleUpdate(u.id, 'sub_admin')}
                                        data-testid={`promote-subadmin-${u.id}`}
                                        className="text-amber-700 hover:underline text-sm flex items-center gap-1"
                                      >
                                        <Shield className="w-3.5 h-3.5" /> {t.promoteSubAdmin}
                                      </button>
                                      <button
                                        onClick={() => handleRoleUpdate(u.id, 'partner')}
                                        data-testid={`promote-partner-${u.id}`}
                                        className="text-[#D4AF37] hover:underline text-sm flex items-center gap-1"
                                      >
                                        <Sparkles className="w-3.5 h-3.5" /> {t.promotePartner}
                                      </button>
                                      <button
                                        onClick={() => handleRoleUpdate(u.id, 'admin')}
                                        data-testid={`promote-user-${u.id}`}
                                        className="text-purple-600 hover:underline text-sm flex items-center gap-1"
                                      >
                                        <Shield className="w-3.5 h-3.5" /> {t.promoteAdmin}
                                      </button>
                                    </>
                                  )}
                                  {u.role === 'partner' && (
                                    <button
                                      onClick={() => handleRoleUpdate(u.id, 'user')}
                                      data-testid={`revoke-partner-${u.id}`}
                                      className="text-gray-600 hover:underline text-sm flex items-center gap-1"
                                    >
                                      <Users className="w-3.5 h-3.5" /> {t.revokePartner}
                                    </button>
                                  )}
                                  {u.role === 'sub_admin' && (
                                    <>
                                      <button
                                        onClick={() => handleRoleUpdate(u.id, 'user')}
                                        data-testid={`revoke-subadmin-${u.id}`}
                                        className="text-gray-600 hover:underline text-sm flex items-center gap-1"
                                      >
                                        <Users className="w-3.5 h-3.5" /> {t.revokeSubAdmin}
                                      </button>
                                      <button
                                        onClick={() => handleRoleUpdate(u.id, 'admin')}
                                        data-testid={`promote-user-${u.id}`}
                                        className="text-purple-600 hover:underline text-sm flex items-center gap-1"
                                      >
                                        <Shield className="w-3.5 h-3.5" /> {t.promoteAdmin}
                                      </button>
                                    </>
                                  )}
                                  {u.role === 'admin' && (
                                    <button
                                      onClick={() => handleRoleUpdate(u.id, 'user')}
                                      data-testid={`demote-user-${u.id}`}
                                      className="text-gray-600 hover:underline text-sm flex items-center gap-1"
                                    >
                                      <Users className="w-3.5 h-3.5" /> {t.demoteUser}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteUser(u.id, u.email)}
                                    onBlur={() => setConfirmingDelete(null)}
                                    data-testid={`delete-user-${u.id}`}
                                    className={`text-sm flex items-center gap-1 transition-all ${
                                      confirmingDelete === u.id
                                        ? 'bg-red-600 text-white px-3 py-1 rounded-lg font-bold'
                                        : 'text-red-600 hover:underline'
                                    }`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    {confirmingDelete === u.id ? t.confirmDelete : t.deleteUser}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* Deleted Users */
              deletedUsers.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                  <UserX className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">{t.noDeletedUsers}</p>
                </div>
              ) : (
                <div data-testid="deleted-users-table" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-red-400 uppercase">{t.name}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-red-400 uppercase">{t.email}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-red-400 uppercase">{t.role}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-red-400 uppercase">{t.actions}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {deletedUsers.map((u, i) => (
                        <tr key={u.id} data-testid={`deleted-user-row-${i}`} className="hover:bg-gray-50 bg-red-50/30">
                          <td className="px-5 py-3 text-sm font-medium text-gray-400">{u.first_name} {u.last_name}</td>
                          <td className="px-5 py-3 text-sm text-gray-400">{u.email}</td>
                          <td className="px-5 py-3">
                            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-600">
                              {language === 'fr' ? 'Supprime' : 'Deleted'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            {!isSubAdmin ? (
                              <button
                                onClick={() => handleRestoreUser(u.id)}
                                data-testid={`restore-user-${u.id}`}
                                className="text-[#D4AF37] hover:underline text-sm flex items-center gap-1 font-bold"
                              >
                                <RotateCcw className="w-3.5 h-3.5" /> {t.restoreUser}
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}

        {/* ========== PARTNER PAYOUTS TAB ========== */}
        {activeTab === 'partnerPayouts' && !isSubAdmin && (
          <div className="space-y-4" data-testid="admin-partner-payouts">
            <div>
              <h2 className="text-xl font-extrabold text-[#1D1D1D]">
                {language === 'fr' ? 'Commissions partenaires' : 'Partner Payouts'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {language === 'fr'
                  ? "Marquez les commissions des partenaires comme payées une fois le virement effectué."
                  : 'Mark partner commissions as paid once you have completed the bank transfer.'}
              </p>
            </div>
            {partnerPayouts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">{language === 'fr' ? 'Aucune commission encore.' : 'No commissions yet.'}</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{language === 'fr' ? 'Partenaire' : 'Partner'}</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{language === 'fr' ? 'Référé' : 'Referral'}</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{language === 'fr' ? 'Montant' : 'Amount'}</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.status}</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.date}</th>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {partnerPayouts.map((p) => (
                      <tr key={p.id} data-testid={`admin-payout-row-${p.id}`} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm font-medium">
                          {p.partner?.first_name} {p.partner?.last_name}
                          <p className="text-xs text-gray-400">{p.partner?.email}</p>
                          {p.partner?.referral_code && (
                            <code className="text-[10px] font-mono text-[#D4AF37]">{p.partner.referral_code}</code>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm">
                          {p.referred_user?.first_name} {p.referred_user?.last_name}
                          <p className="text-xs text-gray-400">{p.referred_user?.email}</p>
                          {p.is_proxy_payment && (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                              {language === 'fr' ? 'Paiement par partenaire' : 'Paid by partner'}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-sm font-bold text-[#1D1D1D]">${(p.commission_usd || 0).toFixed(2)}</td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-bold border ${
                            p.status === 'paid'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            {p.status === 'paid' ? (language === 'fr' ? 'Payée' : 'Paid') : (language === 'fr' ? 'En attente' : 'Pending')}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                        <td className="px-5 py-3">
                          <button
                            data-testid={`admin-payout-toggle-${p.id}`}
                            onClick={() => handleTogglePayoutStatus(p)}
                            className={`text-sm font-bold hover:underline flex items-center gap-1 ${
                              p.status === 'paid' ? 'text-amber-700' : 'text-emerald-700'
                            }`}
                          >
                            {p.status === 'paid'
                              ? (language === 'fr' ? 'Annuler' : 'Revert')
                              : (language === 'fr' ? 'Marquer payée' : 'Mark Paid')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ========== INVITATIONS TAB ========== */}
        {activeTab === 'invitations' && (() => {
          const paidRegs = registrations.filter(r => r.status === 'paid');
          const pendingInvites = paidRegs.filter(r => !r.invitation_status || r.invitation_status === 'pending');
          const sentInvites = paidRegs.filter(r => r.invitation_status === 'sent');
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-extrabold text-[#1D1D1D]">{t.invitationsTitle}</h2>
                  <p className="text-gray-500 text-sm mt-1">{t.invitationsSubtitle}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <button
                    data-testid="dispatch-certificates-btn"
                    type="button"
                    onClick={handleDispatchCertificates}
                    className="px-4 py-2.5 rounded-xl text-sm font-bold transition-colors bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:shadow-lg flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" /> {language === 'fr' ? 'Envoyer Certificats' : 'Send Certificates'}
                  </button>
                  <button
                    data-testid="preview-sample-invitation-btn"
                    type="button"
                    onClick={handlePreviewSample}
                    className="px-4 py-2.5 rounded-xl text-sm font-bold transition-colors bg-[#CD0000] text-white hover:bg-[#A00000] flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> {language === 'fr' ? 'Aperçu Échantillon' : 'Preview Sample'}
                  </button>
                  <label data-testid="upload-signature-btn" className="px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-colors bg-[#1D1D1D] text-white hover:bg-[#333] flex items-center gap-2">
                    <Upload className="w-4 h-4" /> {t.uploadSignature}
                    <input type="file" accept="image/png,image/jpeg" onChange={handleSignatureUpload} className="hidden" />
                  </label>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
                  <Clock className="w-7 h-7 text-amber-600" />
                  <div>
                    <p className="text-2xl font-extrabold text-amber-800">{pendingInvites.length}</p>
                    <p className="text-xs text-amber-600">{t.pendingInvitation}</p>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                  <Send className="w-7 h-7 text-green-600" />
                  <div>
                    <p className="text-2xl font-extrabold text-green-800">{sentInvites.length}</p>
                    <p className="text-xs text-green-600">{t.sentInvitation}</p>
                  </div>
                </div>
              </div>

              {paidRegs.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">{t.noInvitations}</p>
                </div>
              ) : (
                <div data-testid="invitations-table" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.name}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.email}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{language === 'fr' ? 'Nationalité' : 'Nationality'}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{language === 'fr' ? 'Visa demandé' : 'Visa Request'}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.invitation}</th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.actions}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paidRegs.map((reg, i) => (
                        <tr key={reg.id} data-testid={`invitation-row-${i}`} className="hover:bg-gray-50">
                          <td className="px-5 py-3 text-sm font-medium">{reg.user?.first_name} {reg.user?.last_name}</td>
                          <td className="px-5 py-3 text-sm text-gray-500">{reg.user?.email}</td>
                          <td className="px-5 py-3 text-sm text-gray-500">{reg.nationality || '-'}</td>
                          <td className="px-5 py-3">
                            {reg.needs_visa_letter ? (
                              <span className="bg-[#CD0000]/10 text-[#CD0000] text-xs font-bold px-2.5 py-1 rounded-full">
                                {language === 'fr' ? 'Oui' : 'Yes'}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">{language === 'fr' ? 'Non' : 'No'}</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              reg.invitation_status === 'sent'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {reg.invitation_status === 'sent' ? t.invitationSent : t.invitationPending}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex gap-2 flex-wrap">
                              <button onClick={() => setSelectedRegistration(reg)} className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                                <Eye className="w-3.5 h-3.5" /> {t.view}
                              </button>
                              {(!reg.invitation_status || reg.invitation_status === 'pending') && (
                                <button
                                  onClick={() => handleInvitationUpdate(reg.id, 'approved')}
                                  data-testid={`mark-invitation-sent-${reg.id}`}
                                  className="text-green-600 hover:underline text-sm flex items-center gap-1"
                                >
                                  <Send className="w-3.5 h-3.5" /> {t.markInvitationSent}
                                </button>
                              )}
                              {reg.invitation_status === 'sent' && (
                                <button
                                  onClick={() => handleResendInvitation(reg.id, reg.user?.email)}
                                  data-testid={`resend-invitation-${reg.id}`}
                                  className="text-[#CD0000] hover:underline text-sm flex items-center gap-1 font-semibold"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" /> {t.resendInvitation}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

      {/* Registration Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-extrabold">{t.registrationDetails}</h2>
                <button onClick={() => setSelectedRegistration(null)} className="text-gray-400 hover:text-gray-600 w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Personal */}
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Informations Personnelles' : 'Personal Information'}</h4>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  [t.name, `${selectedRegistration.user?.first_name} ${selectedRegistration.user?.last_name}`],
                  [t.email, selectedRegistration.user?.email],
                  [t.phone, selectedRegistration.user?.phone],
                  [language === 'fr' ? 'Date de Naissance' : 'Date of Birth', selectedRegistration.date_of_birth || selectedRegistration.age || '-'],
                  [language === 'fr' ? 'Genre' : 'Gender', selectedRegistration.gender || '-'],
                  [language === 'fr' ? 'Nationalité' : 'Nationality', selectedRegistration.nationality || '-'],
                  [language === 'fr' ? 'Lieu de Naissance' : 'Place of Birth', selectedRegistration.place_of_birth || '-'],
                  [language === 'fr' ? 'No. Passeport' : 'Passport No.', selectedRegistration.passport_number || '-'],
                  [language === 'fr' ? 'Expiration Passeport' : 'Passport Expiry', selectedRegistration.passport_expiry || '-'],
                ].map(([label, val], i) => (
                  <div key={i}>
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="font-medium text-sm">{val}</p>
                  </div>
                ))}
              </div>

              {/* Address & Professional */}
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Adresse et Profession' : 'Address & Professional'}</h4>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  [language === 'fr' ? 'Adresse' : 'Address', selectedRegistration.address || '-'],
                  [t.city, selectedRegistration.city],
                  [language === 'fr' ? 'Province' : 'State/Province', selectedRegistration.state_province || '-'],
                  [language === 'fr' ? 'Code Postal' : 'Postal Code', selectedRegistration.postal_code || '-'],
                  [t.country, selectedRegistration.country],
                  [t.occupation, selectedRegistration.occupation],
                  [language === 'fr' ? 'Organisation' : 'Organization', selectedRegistration.organization || '-'],
                ].map(([label, val], i) => (
                  <div key={i}>
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="font-medium text-sm">{val}</p>
                  </div>
                ))}
              </div>

              {/* Travel / Visa */}
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Voyage et Visa' : 'Travel & Visa'}</h4>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  [language === 'fr' ? 'Arrivee' : 'Arrival', selectedRegistration.arrival_date || '-'],
                  [language === 'fr' ? 'Depart' : 'Departure', selectedRegistration.departure_date || '-'],
                  [language === 'fr' ? 'Lettre Visa' : 'Visa Letter', selectedRegistration.needs_visa_letter ? (language === 'fr' ? 'Oui' : 'Yes') : (language === 'fr' ? 'Non' : 'No')],
                  [language === 'fr' ? 'Ambassade' : 'Embassy City', selectedRegistration.visa_embassy_city || '-'],
                  [language === 'fr' ? 'Pays Visa' : 'Visa Country', selectedRegistration.visa_country || '-'],
                ].map(([label, val], i) => (
                  <div key={i}>
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="font-medium text-sm">{val}</p>
                  </div>
                ))}
              </div>

              {/* Conference Details */}
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Conference' : 'Conference Details'}</h4>
              <div className="space-y-3 mb-6">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">{t.reason}</p>
                  <p className="font-medium text-sm">{selectedRegistration.reason}</p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    [language === 'fr' ? 'Regime Alimentaire' : 'Dietary', selectedRegistration.dietary_requirements || '-'],
                    [t.heardAbout, selectedRegistration.heard_about || '-'],
                    [t.specialNeeds, selectedRegistration.special_needs || '-'],
                  ].map(([label, val], i) => (
                    <div key={i}>
                      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                      <p className="font-medium text-sm">{val}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Emergency */}
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{t.emergencyContact}</h4>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  [t.emergencyContact, selectedRegistration.emergency_contact],
                  [t.emergencyPhone, selectedRegistration.emergency_phone],
                  [language === 'fr' ? 'Relation' : 'Relationship', selectedRegistration.emergency_relationship || '-'],
                ].map(([label, val], i) => (
                  <div key={i}>
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="font-medium text-sm">{val}</p>
                  </div>
                ))}
              </div>

              {selectedRegistration.status === 'pending' && (
                <div className="flex flex-col gap-3 pt-6 border-t">
                  <div className="flex gap-3">
                    <button onClick={() => handleStatusUpdate(selectedRegistration.id, 'approved')}
                      className="flex-1 bg-green-600 text-white py-2.5 rounded-xl font-bold hover:bg-green-700 flex items-center justify-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4" /> {t.approve}
                    </button>
                    <button onClick={() => handleStatusUpdate(selectedRegistration.id, 'rejected')}
                      className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-bold hover:bg-red-700 flex items-center justify-center gap-2 text-sm">
                      <XCircle className="w-4 h-4" /> {t.reject}
                    </button>
                  </div>
                  {!showReturnForm ? (
                    <button onClick={() => setShowReturnForm(true)}
                      className="w-full bg-orange-50 border border-orange-200 text-orange-700 py-2.5 rounded-xl font-bold hover:bg-orange-100 flex items-center justify-center gap-2 text-sm">
                      <Edit className="w-4 h-4" /> {t.returnIncomplete}
                    </button>
                  ) : (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
                      <label className="block text-sm font-bold text-orange-800">{t.returnNote}</label>
                      <textarea
                        value={incompleteNote}
                        onChange={(e) => setIncompleteNote(e.target.value)}
                        placeholder={t.returnNotePlaceholder}
                        rows={3}
                        data-testid="incomplete-note-input"
                        className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReturnIncomplete(selectedRegistration.id)}
                          disabled={!incompleteNote.trim()}
                          data-testid="submit-return-incomplete"
                          className="flex-1 bg-orange-600 text-white py-2 rounded-lg font-bold text-sm hover:bg-orange-700 disabled:opacity-50"
                        >
                          {t.returnSubmit}
                        </button>
                        <button onClick={() => { setShowReturnForm(false); setIncompleteNote(''); }}
                          className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-bold hover:bg-gray-200">
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {selectedRegistration.status === 'approved' && (
                <div className="pt-6 border-t">
                  <button
                    onClick={() => handleExemptPayment(selectedRegistration.id, `${selectedRegistration.user?.first_name} ${selectedRegistration.user?.last_name}`)}
                    data-testid="modal-exempt-payment-btn"
                    className="w-full bg-purple-50 border border-purple-200 text-purple-700 py-2.5 rounded-xl font-bold hover:bg-purple-100 flex items-center justify-center gap-2 text-sm"
                  >
                    <CheckCircle className="w-4 h-4" /> {t.exemptPayment}
                  </button>
                  <p className="text-gray-400 text-xs text-center mt-2">
                    {language === 'fr' ? 'Accorde un accès gratuit sans exiger de paiement (discrétion admin)' : 'Grants complimentary access without requiring payment (admin discretion)'}
                  </p>
                </div>
              )}
              {selectedRegistration.status === 'incomplete' && (
                <div className="pt-6 border-t space-y-3">
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-1">{language === 'fr' ? 'Note admin' : 'Admin Note'}</p>
                    <p className="text-sm text-orange-800">{selectedRegistration.admin_note}</p>
                  </div>
                  <p className="text-gray-400 text-xs text-center">{language === 'fr' ? 'En attente de correction par le participant' : 'Waiting for attendee to update and resubmit'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Participation History Modal */}
      {viewingUserHistory && (
        <div data-testid="user-history-modal" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewingUserHistory(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-extrabold text-[#1D1D1D]">{t.userHistoryTitle}</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {viewingUserHistory.user?.first_name} {viewingUserHistory.user?.last_name} · {viewingUserHistory.user?.email}
                </p>
              </div>
              <button onClick={() => setViewingUserHistory(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              {viewingUserHistory.history.length === 0 ? (
                <div className="text-center py-10">
                  <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">{t.noHistory}</p>
                </div>
              ) : (
                viewingUserHistory.history.map((h) => (
                  <div key={h.registration_id} data-testid={`admin-history-item-${h.registration_id}`} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <h4 className="font-bold text-[#1D1D1D] text-sm">{language === 'fr' ? (h.event_title_fr || h.event_title) : h.event_title}</h4>
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          h.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                          h.status === 'approved' ? 'bg-green-100 text-green-800' :
                          h.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          h.status === 'incomplete' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {h.is_past_event && h.status === 'paid' ? (language === 'fr' ? 'Participé' : 'Attended') : h.status}
                        </span>
                        {h.payment_exempted && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                            {language === 'fr' ? 'Exempté' : 'Exempted'}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${h.is_past_event ? 'bg-gray-100 text-gray-600' : 'bg-sky-50 text-sky-700'}`}>
                          {h.is_past_event ? (language === 'fr' ? 'Passé' : 'Past') : (language === 'fr' ? 'À venir' : 'Upcoming')}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{h.start_date} → {h.end_date}{h.venue ? ` · ${h.venue}` : ''}</p>
                    <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
                      {h.created_at && (
                        <p className="text-xs text-gray-400">
                          {language === 'fr' ? 'Inscrit(e) le' : 'Registered on'}: {new Date(h.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                        </p>
                      )}
                      {h.attended && h.is_past_event && (
                        <a
                          href={`${BACKEND_URL}/api/participations/${h.registration_id}/certificate`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const res = await axios.get(`${BACKEND_URL}/api/participations/${h.registration_id}/certificate`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                              const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Certificate_${h.registration_id}.pdf`;
                              document.body.appendChild(a); a.click(); a.remove();
                              URL.revokeObjectURL(url);
                            } catch (err) { alert('Error downloading certificate'); }
                          }}
                          data-testid={`admin-download-certificate-${h.registration_id}`}
                          className="inline-flex items-center gap-1.5 bg-[#CD0000] hover:bg-[#A00000] text-white font-bold text-xs px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" /> {language === 'fr' ? 'Télécharger Certificat' : 'Download Certificate'}
                        </a>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

        </main>
      </div>

      {/* Admin Edit User Modal */}
      <AdminEditUserModal
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        user={editingUser}
        token={token}
        language={language}
        currentUserRole={user?.role}
        onSaved={() => fetchAllUsers && fetchAllUsers()}
      />

      {/* Admin Edit Registration Modal */}
      <AdminEditRegistrationModal
        open={!!editingRegistration}
        onClose={() => setEditingRegistration(null)}
        registration={editingRegistration}
        attendee={editingRegistration?.user || allUsers.find(u => u.id === editingRegistration?.user_id)}
        token={token}
        language={language}
        onSaved={() => fetchRegistrations()}
      />

      {/* Create Partner Modal — admin creates a "pure" partner (no conference registration) */}
      <CreatePartnerModal
        open={creatingPartner}
        onClose={() => setCreatingPartner(false)}
        token={token}
        language={language}
        onCreated={() => fetchAllUsers && fetchAllUsers()}
      />

      {/* Postponement Campaign Modal — preview + send + reissue-all */}
      <PostponementCampaignModal
        open={showPostponement}
        onClose={() => setShowPostponement(false)}
        token={token}
        language={language}
      />
    </div>
  );
};

export default AdminDashboard;
